'use client'

import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { usePathname } from 'next/navigation'
import { useStytchMember } from '@stytch/nextjs/b2b'
import { getAllFeatureFlags } from '@/services/patient/featureFlags'
import { isOnboardingCookieSetClient } from '@/utilities/onboardingCookie'
import { SHARED_PATHS } from '@/utilities/paths'
import type { ClientFeatureFlagContext } from '@/utilities/client/clientPortalContext'

type FeatureFlags = Record<string, unknown>

interface FeatureFlagsContextType {
  flags: FeatureFlags
  isLoading: boolean
  hasFetched: boolean
  isProvisional: boolean
  error: string | null
  clientContext?: ClientFeatureFlagContext
  refetch: () => Promise<void>
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined)

interface FeatureFlagsProviderProps {
  children: ReactNode
}

export type ClientFeatureFlagsState =
  | { kind: 'loading' }
  | {
      kind: 'ready'
      context: ClientFeatureFlagContext
      flags: FeatureFlags
      error: string | null
      refetch: () => Promise<void>
    }

const refetchUnavailable = () => Promise.resolve()

export function ClientFeatureFlagsProvider({
  children,
  enabled,
  state,
}: Readonly<{
  children: ReactNode
  enabled: boolean
  state: ClientFeatureFlagsState
}>) {
  const value = useMemo<FeatureFlagsContextType>(() => {
    if (state.kind === 'loading') {
      return {
        flags: {},
        isLoading: true,
        hasFetched: false,
        isProvisional: false,
        error: null,
        clientContext: undefined,
        refetch: refetchUnavailable,
      }
    }

    return {
      flags: state.flags,
      isLoading: false,
      hasFetched: state.error === null,
      isProvisional: false,
      error: state.error,
      clientContext: state.context,
      refetch: state.refetch,
    }
  }, [state])

  if (!enabled) return children
  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}

export function LaunchDarklyProvider({ children }: Readonly<FeatureFlagsProviderProps>) {
  const [flags, setFlags] = useState<FeatureFlags>({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // hasFinalFetch is the internal effect trigger: true only when a non-provisional
  // (final) result has been received. It drives the re-fetch effect gate so that
  // provisional results leave the gate open for pathname-change upgrades without
  // causing an immediate re-fire.
  const [hasFinalFetch, setHasFinalFetch] = useState(false)
  const [isProvisional, setIsProvisional] = useState(false)
  const { member, isInitialized } = useStytchMember()
  const pathname = usePathname()
  const isClientPortalPath = pathname.startsWith('/client')
  const latestFetchRequestRef = useRef(0)

  const getStorageKey = useCallback((memberId: string) => `featureFlags_${memberId}`, [])

  // Load cached flags from localStorage
  const loadCachedFlags = useCallback(
    (memberId: string) => {
      try {
        const cached = localStorage.getItem(getStorageKey(memberId))
        if (cached) {
          const parsedFlags = JSON.parse(cached)
          if (parsedFlags && typeof parsedFlags === 'object') {
            const hasAnyFlags = Object.keys(parsedFlags as Record<string, unknown>).length > 0
            if (hasAnyFlags) {
              setFlags(parsedFlags)
              return true
            }
          }
          return false
        }
      } catch (error) {
        console.warn('Failed to load cached feature flags:', error)
      }
      return false
    },
    [getStorageKey],
  )

  // Save flags to localStorage
  const saveFlagsToStorage = useCallback(
    (memberId: string, flagsToSave: FeatureFlags) => {
      try {
        localStorage.setItem(getStorageKey(memberId), JSON.stringify(flagsToSave))
      } catch (error) {
        console.warn('Failed to save feature flags to localStorage:', error)
      }
    },
    [getStorageKey],
  )

  // Load cached flags and reset state when member changes.
  useEffect(() => {
    latestFetchRequestRef.current += 1
    if (!member?.member_id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- state update required inside this async/side-effect; cannot be derived
      setFlags({})
      setHasFinalFetch(false)
      setIsProvisional(false)
      return
    }

    if (isClientPortalPath) {
      setFlags({})
      setHasFinalFetch(false)
      setIsProvisional(false)
      return
    }

    const hasCachedFlags = loadCachedFlags(member.member_id)
    if (!hasCachedFlags) {
      setFlags({})
    }
    const isOnboarding = isOnboardingCookieSetClient()
    // Cached flags from an onboarding session are provisional; leave hasFinalFetch
    // false so the re-fetch effect can upgrade once registration completes.
    setHasFinalFetch(hasCachedFlags && !isOnboarding)
    setIsProvisional(hasCachedFlags && isOnboarding)
  }, [member?.member_id, isClientPortalPath, loadCachedFlags])

  const fetchFlags = useCallback(async () => {
    const requestId = ++latestFetchRequestRef.current
    setIsLoading(true)
    setError(null)

    try {
      const result = await getAllFeatureFlags()
      if (requestId !== latestFetchRequestRef.current) return
      setFlags(result.flags)
      setIsProvisional(result.error ? false : result.provisional)
      setError(result.error ?? null)
      if (!result.provisional && !result.error) {
        setHasFinalFetch(true)
      }
    } catch (err) {
      if (requestId !== latestFetchRequestRef.current) return
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch feature flags'
      setError(errorMessage)
      console.error('Error fetching feature flags:', err)
    } finally {
      if (requestId === latestFetchRequestRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  // Persist flags once we know the member id (handles the "flags fetched before member_id exists" race)
  useEffect(() => {
    if (!member?.member_id || isClientPortalPath) return
    saveFlagsToStorage(member.member_id, flags)
  }, [member?.member_id, flags, isClientPortalPath, saveFlagsToStorage])

  // Fetch flags when authenticated and we haven't received a final result yet.
  // Skip post-auth pages (/authenticate, /login) where the onboarding cookie
  // may not have been written yet.
  //
  // pathname drives the provisional upgrade loop: each navigation re-evaluates
  // this effect; hasFinalFetch stays false while results are provisional so the
  // loop keeps firing on navigation until registration completes and the API
  // returns provisional=false.
  useEffect(() => {
    const isPostAuthPage =
      pathname.startsWith(SHARED_PATHS.authenticate) || pathname.startsWith(SHARED_PATHS.login)

    const shouldFetch =
      !isClientPortalPath && isInitialized && member && !hasFinalFetch && !isPostAuthPage
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (shouldFetch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- state update required inside this async/side-effect; cannot be derived
      fetchFlags()
    }
  }, [isInitialized, member, hasFinalFetch, fetchFlags, pathname, isClientPortalPath])

  const handleRefetch = useCallback(async () => {
    await fetchFlags()
  }, [fetchFlags])

  // hasFetched exposed to consumers: true once any fetch has completed (provisional or final).
  // Separate from hasFinalFetch so consumers can know "we have some flags" while
  // isProvisional tells them the result may still be upgraded.
  const hasFetched = hasFinalFetch || isProvisional

  const value = useMemo<FeatureFlagsContextType>(
    () => ({
      flags,
      isLoading,
      hasFetched,
      isProvisional,
      error,
      refetch: handleRefetch,
    }),
    [flags, isLoading, hasFetched, isProvisional, error, handleRefetch],
  )

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}

export function useLaunchDarkly() {
  const context = useContext(FeatureFlagsContext)
  if (context === undefined) {
    throw new Error('useLaunchDarkly must be used within a LaunchDarklyProvider')
  }
  return context
}

// Type-safe flag access hook
export function useFeatureFlag<T>(flagKey: string): T | undefined
export function useFeatureFlag<T>(flagKey: string, defaultValue: T): T
export function useFeatureFlag<T>(flagKey: string, defaultValue?: T): T | undefined {
  const { flags } = useLaunchDarkly()
  return (flags?.[flagKey] as T | undefined) ?? defaultValue
}

function getFeatureFlagJsonEnabledValue(flagValue: unknown): boolean | undefined {
  let parsed: unknown = flagValue

  if (typeof flagValue === 'string') {
    try {
      parsed = JSON.parse(flagValue)
    } catch {
      return undefined
    }
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return undefined
  }

  const enabled = (parsed as Record<string, unknown>).enabled
  return typeof enabled === 'boolean' ? enabled : undefined
}

export function isFeatureFlagEnabledFromJson(flagValue: unknown): boolean {
  return getFeatureFlagJsonEnabledValue(flagValue) === true
}

export function isFeatureFlagJsonValueResolved(flagValue: unknown): boolean {
  return getFeatureFlagJsonEnabledValue(flagValue) !== undefined
}

export function useFeatureFlagEnabledFromJSON(flagKey: string): boolean {
  const flagValue = useFeatureFlag(flagKey)
  return isFeatureFlagEnabledFromJson(flagValue)
}

// Booleanize the flag value
const disabledValues = ['disabled', 'hidden', 'hide', 'ignore', 'ignored', 'off', 'false']
export function useIfEnabled(flagKey: string): boolean {
  const flagValue = useFeatureFlag<object | string | number | boolean | undefined>(
    flagKey,
    undefined,
  )
  const jsonEnabledValue = getFeatureFlagJsonEnabledValue(flagValue)
  if (jsonEnabledValue !== undefined) return jsonEnabledValue

  return typeof flagValue === 'string'
    ? !disabledValues.includes(flagValue)
    : Boolean(flagValue) || flagValue === 0
}

// Hook to get loading state and error for feature flags
export function useFeatureFlagsStatus() {
  const { isLoading, error, refetch } = useLaunchDarkly()
  return { isLoading, error, refetch }
}
